// ===========================================
// Project Card Component
// ===========================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FolderKanban, 
  Clock, 
  DollarSign, 
  Github,
  Globe,
  TrendingUp
} from 'lucide-react';
import './ProjectCard.scss';

// Status badge colors and labels
const statusConfig = {
  idea: { color: 'var(--color-status-idea)', label: 'Idea' },
  active: { color: 'var(--color-status-active)', label: 'Active' },
  paused: { color: 'var(--color-status-paused)', label: 'Paused' },
  completed: { color: 'var(--color-status-completed)', label: 'Completed' },
  archived: { color: 'var(--color-status-archived)', label: 'Archived' }
};

const ProjectCard = ({ project, compact = false, listView = false }) => {
  const navigate = useNavigate();
  const status = statusConfig[project.status] || statusConfig.idea;

  // Format relative time
  const getRelativeTime = (date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
  };

  // Navigate to project detail
  const handleClick = () => {
    navigate(`/dashboard/projects/${project._id || project.id}`);
  };

  // Compact view (for recent projects)
  if (compact) {
    return (
      <div className="project-card project-card--compact" onClick={handleClick}>
        <div className="project-card__icon">
          <FolderKanban size={20} />
        </div>
        <div className="project-card__info">
          <h4 className="project-card__name">{project.name}</h4>
          <span className="project-card__time">
            <Clock size={12} />
            {getRelativeTime(project.lastFocused)}
          </span>
        </div>
        <span 
          className="project-card__status-dot"
          style={{ background: status.color }}
        />
      </div>
    );
  }

  // List view
  if (listView) {
    return (
      <div className="project-card project-card--list" onClick={handleClick}>
        <div className="project-card__left">
          <div 
            className="project-card__status-indicator"
            style={{ background: status.color }}
          />
          <div className="project-card__info">
            <h4 className="project-card__name">{project.name}</h4>
            {project.description && (
              <p className="project-card__description">{project.description}</p>
            )}
          </div>
        </div>
        
        <div className="project-card__meta">
          <span className="project-card__badge" style={{ background: `${status.color}20`, color: status.color }}>
            {status.label}
          </span>
          
          {/* ARR Badge for list view */}
          {project.arrLevel && project.arrLevel !== '$0' && (
            <span className="project-card__arr-badge">
              <TrendingUp size={14} />
              {project.arrLevel}
            </span>
          )}
          
          {project.servicesCount > 0 && (
            <span className="project-card__services">
              {project.servicesCount} services
            </span>
          )}
          
          {/* Show total investment in list view */}
          {(project.totalMonthlyCost > 0 || project.aiDevTokenSpend > 0) && (
            <span className="project-card__cost">
              <DollarSign size={14} />
              {((project.totalMonthlyCost || 0) + (project.aiDevTokenSpend || 0)).toFixed(0)}/mo
            </span>
          )}
          
          <span className="project-card__time">
            <Clock size={14} />
            {getRelativeTime(project.lastFocused)}
          </span>
        </div>
      </div>
    );
  }

  // Default grid view
  return (
    <div className="project-card" onClick={handleClick}>
      {/* Header */}
      <div className="project-card__header">
        <div className="project-card__icon-wrapper">
          <FolderKanban size={20} />
        </div>
        <span 
          className="project-card__badge"
          style={{ background: `${status.color}20`, color: status.color }}
        >
          {status.label}
        </span>
      </div>

      {/* Content */}
      <div className="project-card__content">
        <h3 className="project-card__name">{project.name}</h3>
        {project.description && (
          <p className="project-card__description">{project.description}</p>
        )}
        
        {/* Tech Stack */}
        {project.techStack && project.techStack.length > 0 && (
          <div className="project-card__tech">
            {project.techStack.slice(0, 4).map((tech, i) => (
              <span key={i} className="project-card__tech-tag">{tech}</span>
            ))}
            {project.techStack.length > 4 && (
              <span className="project-card__tech-more">
                +{project.techStack.length - 4}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="project-card__footer">
        <div className="project-card__stats">
          {/* ARR Badge - Show for revenue-generating projects */}
          {project.arrLevel && project.arrLevel !== '$0' && (
            <span className="project-card__stat project-card__arr">
              <TrendingUp size={14} />
              {project.arrLevel}
            </span>
          )}
          {project.servicesCount > 0 && (
            <span className="project-card__stat">
              {project.servicesCount} services
            </span>
          )}
          {/* Show total investment (infra + AI spend) */}
          {(project.totalMonthlyCost > 0 || project.aiDevTokenSpend > 0) && (
            <span className="project-card__stat project-card__cost">
              <DollarSign size={14} />
              {((project.totalMonthlyCost || 0) + (project.aiDevTokenSpend || 0)).toFixed(0)}/mo
            </span>
          )}
        </div>
        
        <div className="project-card__links">
          {project.repoUrl && (
            <a 
              href={project.repoUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="project-card__link"
              title="Repository"
            >
              <Github size={16} />
            </a>
          )}
          {project.liveUrl && (
            <a 
              href={project.liveUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="project-card__link"
              title="Live Site"
            >
              <Globe size={16} />
            </a>
          )}
        </div>
      </div>

      {/* Last Focused */}
      <div className="project-card__last-focused">
        <Clock size={12} />
        <span>{getRelativeTime(project.lastFocused)}</span>
      </div>
    </div>
  );
};

export default ProjectCard;

